import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { api } from "../../lib/api";
import { TabConfig } from "../agent/TabConfig";
import { DialogPanel } from "../shared/DialogPanel";
import { UnsavedGuard, type LeaveController } from "../agent/unsavedGuard";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";
import { globalTag } from "../agent/SelfService";

const STATUS_PT: Record<string, string> = { development: "Em desenvolvimento", active: "Produção", inactive: "Inativo" };

interface AgentTemplate {
  id: string;
  name: string;
  template_description: string | null;
  model_provider: string;
  model_name: string;
  template_kind?: string;
  status?: string;
}

const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

export function AgentTemplatesTab({ readOnly = false }: { readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery<AgentTemplate[]>({
    queryKey: ["dev-agent-templates"],
    queryFn: () => api.get("/dev/agent-templates"),
  });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [delTpl, setDelTpl] = useState<AgentTemplate | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/agents/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dev-agent-templates"] }); setDelTpl(null); },
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
          Templates de Agentes
          {readOnly && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, padding: "2px 8px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-muted)", border: "1px solid var(--line)" }}>somente leitura</span>}
        </span>
        {!readOnly && <button
          onClick={() => setCreating(true)}
          style={{ padding: "7px 14px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}
        >
          + Novo template
        </button>}
      </div>

      {templates.length === 0 ? (
        <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "40px 0" }}>Nenhum template de agente cadastrado</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {templates.map((t) => (
            <div
              key={t.id}
              onClick={() => setEditingId(t.id)}
              style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div style={{ minWidth: 0, marginRight: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>{t.name}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
                  <span style={t.template_kind === "global" ? globalTag : { ...globalTag, background: "var(--surface-input)", color: "var(--ink-muted)", borderColor: "var(--line)" }}>
                    {t.template_kind === "global" ? "Global" : "Clone"}
                  </span>
                  {t.template_kind === "global" && <span style={{ fontSize: 11, color: t.status === "active" ? "var(--ok)" : "var(--ink-muted)" }}>{STATUS_PT[t.status ?? ""] ?? t.status}</span>}
                </div>
                {t.template_description && (
                  <div style={{ fontSize: 12, color: "var(--ink-mid)", marginTop: 3 }}>{t.template_description}</div>
                )}
                <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 4, fontFamily: "var(--font-mono)" }}>
                  {t.model_provider}/{t.model_name}
                </div>
              </div>
              {!readOnly && <button
                onClick={(e) => { e.stopPropagation(); setDelTpl(t); }}
                style={{ background: "none", border: "none", color: "var(--danger)", fontSize: 12, cursor: "pointer", padding: "4px 8px" }}
              >
                Excluir
              </button>}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <NewTemplateModal
          onClose={() => setCreating(false)}
          onCreated={(id) => { qc.invalidateQueries({ queryKey: ["dev-agent-templates"] }); setCreating(false); setEditingId(id); }}
        />
      )}
      {editingId && (
        <TemplateConfigModal
          templateId={editingId}
          onClose={() => { qc.invalidateQueries({ queryKey: ["dev-agent-templates"] }); setEditingId(null); }}
        />
      )}
      {delTpl && (
        <ConfirmDialog
          title={`Excluir template "${delTpl.name}"?`}
          confirmLabel="Excluir"
          danger
          onConfirm={() => del.mutate(delTpl.id)}
          onCancel={() => setDelTpl(null)}
        />
      )}
    </div>
  );
}

function NewTemplateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [kind, setKind] = useState<"clone" | "global">("clone");

  const mut = useMutation<{ id: string }, any, void>({
    mutationFn: () => api.post("/agents", {
      name: name.trim(), template_description: description.trim() || null,
      model_provider: "openai", model_name: "gpt-4o", is_template: true, template_kind: kind,
    }),
    onSuccess: (data) => onCreated(data.id),
    onError: (e) => setError(e?.payload?.detail ?? "Erro ao criar template"),
  });

  function submit() {
    setError("");
    if (name.trim().length < 2) { setError("Nome deve ter ao menos 2 caracteres."); return; }
    mut.mutate();
  }

  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onClose()} label="Novo template de agente" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 460, display: "flex", flexDirection: "column", gap: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Novo template de agente</span>
        <div>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Nome</label>
          <input style={inp} autoFocus placeholder="Ex: Modelo Vendas" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Descrição (vista na importação)</label>
          <textarea
            style={{ ...inp, height: 64, resize: "vertical", lineHeight: 1.5 }}
            placeholder="O que este modelo de agente faz e quando usá-lo."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Tipo</label>
          <div style={{ display: "flex", gap: 8 }}>
            {([["clone", "Modelo (clone)", "Copiado ao criar um agente de configuração total. Sem vínculo depois."], ["global", "Agente Global", "Implantado como agente simplificado: gestor só preenche os campos editáveis. Publicar aqui atualiza todos."]] as const).map(([k, label, hint]) => (
              <button key={k} type="button" title={hint} onClick={() => setKind(k)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", textAlign: "left", background: kind === k ? "var(--brand-soft)" : "var(--surface-input)", border: `1px solid ${kind === k ? "var(--brand)" : "var(--line-strong)"}`, color: kind === k ? "var(--brand)" : "var(--ink)" }}>
                <div style={{ fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 2 }}>{hint}</div>
              </button>
            ))}
          </div>
        </div>
        {error && <div style={{ fontSize: 12, color: "var(--danger)" }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={submit} disabled={mut.isPending} style={{ padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>
            {mut.isPending ? "Criando..." : "Criar e configurar"}
          </button>
        </div>
      </DialogPanel>
    </div>
  );
}

// Reusa a config de agente inteira (TabConfig) num modal, carregando o template
// pela mesma query key ["agent", id] que os Config* invalidam ao salvar.
function TemplateConfigModal({ templateId, onClose }: { templateId: string; onClose: () => void }) {
  const { data: agent, isLoading } = useQuery<any>({
    queryKey: ["agent", templateId],
    queryFn: () => api.get(`/agents/${templateId}`),
  });
  const ctl = useRef<LeaveController>({ attemptLeave: (run) => run() });
  const requestClose = () => ctl.current.attemptLeave(onClose);

  return (
    <UnsavedGuard controllerRef={ctl}>
      <div {...backdropClose(() => { requestClose(); }, { guarded: true })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
        <DialogPanel onClose={() => requestClose()} label={`Template: ${agent?.name ?? ""}`} style={{ background: "var(--surface-panel)", border: "1px solid var(--line)", borderRadius: 12, width: agent?.template_kind === "global" ? 1440 : 980, maxWidth: "96vw", height: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>
              Template: {agent?.name ?? "…"}
            </span>
            <span style={{ flex: 1 }} />
            {agent?.template_kind === "global" && <GlobalHeader agent={agent} />}
            <button aria-label="Fechar" onClick={requestClose} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
          </div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            {isLoading || !agent ? (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-muted)", fontSize: 13 }}>Carregando…</div>
            ) : (
              <TabConfig agent={agent} />
            )}
          </div>
        </DialogPanel>
      </div>
    </UnsavedGuard>
  );
}

// Agente Global: status (em desenvolvimento = escondido dos gestores; produção =
// implantável; inativo = sai da lista, implantados seguem) e Publicar — que
// atualiza TODOS os implantados na hora, por isso duas confirmações.
function GlobalHeader({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [changelog, setChangelog] = useState("");
  const done = () => { qc.invalidateQueries({ queryKey: ["agent", agent.id] }); qc.invalidateQueries({ queryKey: ["dev-agent-templates"] }); };
  const statusMut = useMutation({ mutationFn: (status: string) => api.patch(`/agents/${agent.id}`, { status }), onSuccess: done });
  const publishMut = useMutation<unknown, any, void>({
    mutationFn: () => api.post(`/agents/${agent.id}/publish`, agent.published_version ? { changelog } : {}),
    onSuccess: () => { done(); setStep(0); setChangelog(""); },
  });
  const republish = !!agent.published_version;
  const canConfirm = !republish || changelog.trim().length >= 30;
  const btn: React.CSSProperties = { padding: "5px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", border: "1px solid var(--line-strong)", background: "var(--surface-input)", color: "var(--ink)" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 12 }}>
      {agent.published_version && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-card)", color: "var(--ink-muted)", border: "1px solid var(--line)" }}>v{agent.published_version}</span>}
      <select value={agent.status} onChange={(e) => statusMut.mutate(e.target.value)} disabled={statusMut.isPending} style={btn}>
        {Object.entries(STATUS_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button onClick={() => setStep(1)} className={agent.has_unpublished_changes ? "publish-pulse" : undefined}
        style={{ ...btn, background: agent.has_unpublished_changes ? "var(--warn-soft)" : "var(--ok-soft)", borderColor: agent.has_unpublished_changes ? "var(--warn-line)" : "var(--ok-line)", color: agent.has_unpublished_changes ? "var(--warn)" : "var(--ok)", fontWeight: 600 }}>
        Publicar
      </button>
      {step > 0 && (
        <div {...backdropClose(() => { setStep(0); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setStep(0)} role="alertdialog" label="Publicar Agente Global" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 460, display: "flex", flexDirection: "column", gap: 14 }}>
            {step === 1 ? (
              <>
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Publicar o Agente Global?</span>
                <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>Prompt, campos editáveis, modelo e tools do global passam a valer em <strong>todos os agentes implantados</strong> a partir dele, sem ação dos gestores.</span>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setStep(0)} style={btn}>Cancelar</button>
                  <button onClick={() => setStep(2)} style={{ ...btn, background: "var(--brand)", color: "#fff", border: "none" }}>Continuar</button>
                </div>
              </>
            ) : (
              <>
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--danger)" }}>Confirmação final</span>
                <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>Isso vai para <strong>produção agora</strong>, em todos os clientes que usam este global. Não há como desfazer em bloco.</span>
                {republish && (
                  <textarea value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="O que mudou nesta versão (mínimo 30 caracteres)" rows={3}
                    style={{ ...inp, resize: "vertical", fontSize: 13 }} />
                )}
                {publishMut.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{publishMut.error?.payload?.detail ?? "Erro ao publicar."}</span>}
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setStep(0)} style={btn}>Cancelar</button>
                  <button onClick={() => publishMut.mutate()} disabled={!canConfirm || publishMut.isPending} style={{ ...btn, background: "var(--danger)", color: "#fff", border: "none", opacity: !canConfirm || publishMut.isPending ? 0.6 : 1 }}>
                    {publishMut.isPending ? "Publicando..." : "Publicar para todos"}
                  </button>
                </div>
              </>
            )}
          </DialogPanel>
        </div>
      )}
    </div>
  );
}
